import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, TextField, Toggle } from './fields';
import EditHistoryLink from './EditHistoryLink';
import AdditionalChargesRatesDrawer from './AdditionalChargesRatesDrawer';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import additionalChargesService, { ChargeFormOptions, ChargeRate } from '../../services/additionalcharges.service';

/**
 * Create/edit drawer for a Transportation Addl Charge (quote.additionalcharges).
 * Master-detail like Fuel Surcharge Tariff: the master holds the description /
 * flags / region / rate type; the effective-dated rates live in the
 * additionalchargerates sub-editor (read-only preview here + an Edit Rates modal).
 * Writes are dev-gated server-side (testquote) — read-only in production for now.
 */
const TABLE = 'additionalcharges';

const EDITABLE = ['description', 'pdfmessage', 'ratetype', 'region', 'transload', 'delivery', 'direct', 'tariff'];
const TOGGLES = [
  { key: 'transload', label: 'Dray' }, // V1 column name is `transload`; displayed as Dray
  { key: 'delivery', label: 'Delivery' },
  { key: 'direct', label: 'Direct' },
  { key: 'tariff', label: 'Tariff' },
];
const RATE_TYPES = [
  { value: '', label: '— None —' },
  { value: 'EXTRA', label: 'Extra' },
  { value: 'EXTRA IN RATE', label: 'Extra In Rate' },
  { value: 'IN RATE', label: 'In Rate' },
  { value: 'ON INVOICE', label: 'On Invoice' },
];
const NEW_DEFAULTS: TablemaintRow = {
  description: '', pdfmessage: '', ratetype: '', region: '',
  transload: 'Y', delivery: 'Y', direct: 'Y', tariff: 'N', // Dray/Delivery/Direct default on
};

const s = (v: any): string => (v == null ? '' : String(v));
const fmtMDY = (v: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s(v));
  return !m || m[1] === '0000' ? '' : `${m[2]}/${m[3]}/${m[1]}`;
};

const AdditionalChargesEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? NEW_DEFAULTS);
  const [form, setForm] = useState<TablemaintRow>({});
  const [formDeleted, setFormDeleted] = useState(false);
  const [options, setOptions] = useState<ChargeFormOptions | null>(null);
  const [rates, setRates] = useState<ChargeRate[]>([]);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const wasDeleted = s(record.deleted) === 'Y';

  const loadRates = useCallback((signal?: AbortSignal) => {
    additionalChargesService.getRates(recno, signal).then((r) => setRates(r.rates)).catch(() => {});
  }, [recno]);

  useEffect(() => {
    const ctrl = new AbortController();
    additionalChargesService.formOptions(ctrl.signal).then(setOptions).catch(() => {});
    if (!isNew) {
      tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
      loadRates(ctrl.signal);
    }
    return () => ctrl.abort();
  }, [isNew, recno, loadRates]);

  // Seed the form from the (full) record.
  useEffect(() => {
    const f: TablemaintRow = {};
    EDITABLE.forEach((k) => { f[k] = s(record[k]); });
    if (isNew) Object.entries(NEW_DEFAULTS).forEach(([k, v]) => { if (!f[k]) f[k] = v as string; });
    setForm(f);
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record, isNew]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const regionOpts = useMemo(
    () => [{ value: '', label: '— None —' }, ...(options?.regions ?? []).map((r) => ({ value: r, label: titleCase(r) }))],
    [options],
  );

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!s(form.description).trim()) errs.push('Description is required.');
    return errs;
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];
  const invalidDesc = showErrors && !s(form.description).trim();

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      const payload: TablemaintRow = {};
      EDITABLE.forEach((k) => { payload[k] = s(form[k]); });
      if (isNew) {
        await tablemaintService.create(TABLE, payload);
      } else {
        const patch: TablemaintRow = {};
        EDITABLE.forEach((k) => { if (s(form[k]) !== s(record[k])) patch[k] = s(form[k]); });
        if (formDeleted !== wasDeleted) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, record, formDeleted, wasDeleted, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Charge' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Transportation Addl Charge'
    : `Edit Charge — ${titleCase(s(record.description))} (#${recno})`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="52rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} label={titleCase(s(record.description))} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Charge */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Charge</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Description" required invalid={invalidDesc} disabled={isSaving}
              value={s(form.description)} onChange={(v) => setField('description', v)} />
            <TextField label="PDF Message" disabled={isSaving}
              value={s(form.pdfmessage)} onChange={(v) => setField('pdfmessage', v)} />
            <SelectField label="Region" disabled={isSaving} value={s(form.region)} options={regionOpts}
              onChange={(v) => setField('region', v)} />
            <SelectField label="Rate Type" disabled={isSaving} clearable={false}
              value={s(form.ratetype)} options={RATE_TYPES} onChange={(v) => setField('ratetype', v)} />
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6">
            {TOGGLES.map((t) => (
              <Toggle key={t.key} label={t.label} disabled={isSaving}
                checked={s(form[t.key]) === 'Y'} onChange={(v) => setField(t.key, v ? 'Y' : 'N')} />
            ))}
          </div>
        </fieldset>

        {/* Rates (read-only preview + editor) — edit mode only */}
        {!isNew && (
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <legend className="px-2 text-sm font-semibold text-label">Rates</legend>
              <button type="button" onClick={() => setRatesOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
                <PencilSquareIcon className="w-4 h-4" /> Edit Rates
              </button>
            </div>
            {rates.length === 0 ? (
              <p className="text-sm text-gray-400">No rates yet — click Edit Rates to add one.</p>
            ) : (
              <table className="text-sm w-full max-w-md">
                <thead>
                  <tr className="text-left text-muted border-b border-gray-200 dark:border-gray-700">
                    <th className="py-1 font-medium">Effective Date</th>
                    <th className="py-1 font-medium text-right">Minimum Rate</th>
                    <th className="py-1 font-medium text-right">Default Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.recno} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <td className="py-1 text-gray-800 dark:text-gray-200">{fmtMDY(r.effectivedate)}</td>
                      <td className="py-1 text-right text-gray-800 dark:text-gray-200">{r.minimumrate}</td>
                      <td className="py-1 text-right text-gray-800 dark:text-gray-200">{r.defaultrate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </fieldset>
        )}

        {/* Deleted */}
        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted} />
            <p className="mt-1 ml-7 text-xs text-muted">
              Soft-deletes the charge — hidden from filters but preserved in history. Uncheck and save to restore.
            </p>
          </div>
        )}
      </div>

      {ratesOpen && (
        <AdditionalChargesRatesDrawer chargeRecno={recno} onClose={() => setRatesOpen(false)}
          onSaved={() => { loadRates(); onSaved(); }} />
      )}
    </Drawer>
  );
};

export default AdditionalChargesEditDrawer;
